// src/dashboardadmin/AdminDashboard.jsx
import React, { useEffect, useMemo, useState } from "react";

/**
 * ============================================================
 * ZONE 1 — SLOTS DES PLATEAUX
 * ============================================================
 *
 * Plateau fromage :
 * - on garde exactement tes slots d’origine
 *
 * Plateau vin :
 * - on garde tes 5 slots actuels
 */
const CHEESE_SLOT_OPTIONS = [
  { id: "p1", label: "Pâte dure / mi-dure" },
  { id: "p2", label: "Fromage persillé" },
  { id: "p3", label: "Brebis / Chèvre" },
  { id: "p4", label: "Croûte lavée" },
  { id: "p5", label: "Pâte molle" },
  { id: "p6", label: "Pâte molle 2" },
  { id: "p7", label: "Fromage persillé 2" },
];

const WINE_SLOT_OPTIONS = [
  { id: "w1", label: "Vin 1" },
  { id: "w2", label: "Vin 2" },
  { id: "w3", label: "Vin 3" },
  { id: "w4", label: "Vin 4" },
  { id: "w5", label: "Vin 5" },
];

/**
 * ============================================================
 * ZONE 2 — MOTS-CLÉS POUR RECONNAÎTRE LA CAVE
 * ============================================================
 *
 * Cette logique sert à :
 * - différencier un produit classique d’un produit cave
 * - afficher des badges
 * - filtrer plus facilement
 * - proposer en priorité les slots vin
 *
 * Tu peux enrichir cette liste plus tard si besoin.
 */
const CAVE_CATEGORY_KEYWORDS = [
  "cave",
  "univers cave",
  "vin",
  "vins",
  "bière",
  "bières",
  "biere",
  "bieres",
  "alcool",
  "alcools",
  "alcool fort",
  "alcools forts",
  "spiritueux",
  "champagne",
  "whisky",
  "rhum",
  "gin",
  "vodka",
  "liqueur",
];

/**
 * ============================================================
 * ZONE 3 — HELPERS
 * ============================================================
 *
 * Petites fonctions utilitaires pour garder le JSX lisible.
 */
function normalizeCategory(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function isCaveCategory(value) {
  const normalized = normalizeCategory(value);
  return CAVE_CATEGORY_KEYWORDS.includes(normalized);
}

function getProductCategory(product) {
  return String(product?.category || product?.categorie || "").trim();
}

/**
 * ============================================================
 * GUIDE — DÉTECTION PRODUIT CAVE
 * ============================================================
 *
 * On ne dépend pas seulement du nom de catégorie.
 * On accepte aussi plusieurs flags si le backend ou le parent
 * les envoie déjà.
 */
function isCaveProduct(product) {
  if (!product) return false;

  if (product?.__isCave === true) return true;
  if (product?.__productScope === "cave") return true;
  if (product?.product_scope === "cave") return true;
  if (product?.source === "cave") return true;
  if (Number(product?.isCave || 0) === 1) return true;

  return isCaveCategory(getProductCategory(product));
}

/**
 * ============================================================
 * GUIDE — ENRICHIR UN PRODUIT AVANT ACTION
 * ============================================================
 *
 * Très important pour tes modales Add / Edit :
 * - un produit cave repart avec les bons flags
 * - un produit classique aussi
 *
 * Comme ça EditProductModal saura choisir :
 * - les catégories cave
 * - le backend cave
 */
function enrichProductWithScope(product) {
  const cave = isCaveProduct(product);

  return {
    ...product,
    __isCave: cave,
    __productScope: cave ? "cave" : "classic",
    product_scope: cave ? "cave" : "classic",
    source: cave ? "cave" : product?.source,
  };
}

const AdminDashboard = ({
  stats = {},
  products = [],
  recherche = "",
  setRecherche,
  categoryFilter = "",
  setCategoryFilter,
  onAdd,
  onSort,
  sortConfig = { key: "name", direction: "asc" },
  onToggleStock,
  onToggleOnline,
  onTogglePromo,
  onEdit,
  onDelete,

  /**
   * ==========================================================
   * NOUVEAU — ACCÈS PAGE BANNIÈRE
   * ==========================================================
   *
   * Ce prop est optionnel pour rester rétrocompatible.
   * Si absent, le dashboard continue de fonctionner comme avant.
   */
  onOpenHomeBanner,
}) => {
  /**
   * ============================================================
   * ZONE 4 — STATE LOCAL DU DASHBOARD
   * ============================================================
   *
   * assignModalOpen : ouverture modale d’assignation plateau
   * assignProduct   : produit en cours d’assignation
   * quickFilter     : filtre visuel rapide pour séparer cave / classique
   */
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assignProduct, setAssignProduct] = useState(null);
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignError, setAssignError] = useState("");
  const [slotMapping, setSlotMapping] = useState({});
  const [quickFilter, setQuickFilter] = useState("all"); // all | classic | cave

  /**
   * ============================================================
   * ZONE 5 — CATÉGORIES DISPONIBLES
   * ============================================================
   *
   * On lit les catégories directement depuis les produits.
   * Cela évite de les coder en dur.
   */
  const categories = useMemo(() => {
    const set = new Set();

    (products || []).forEach((p) => {
      const c = getProductCategory(p);
      if (c) set.add(c);
    });

    return Array.from(set).sort((a, b) => a.localeCompare(b, "fr"));
  }, [products]);

  /**
   * ============================================================
   * ZONE 6 — COMPTEURS INTERNES
   * ============================================================
   *
   * On garde tes stats globales,
   * mais on ajoute des compteurs utiles pour la cave.
   */
  const caveCount = useMemo(
    () => (Array.isArray(products) ? products.filter(isCaveProduct).length : 0),
    [products]
  );

  const classicCount = useMemo(
    () =>
      Array.isArray(products)
        ? products.filter((p) => !isCaveProduct(p)).length
        : 0,
    [products]
  );

  /**
   * ============================================================
   * ZONE 7 — LISTE FILTRÉE
   * ============================================================
   *
   * Ordre des filtres :
   * 1. filtre catégorie (select)
   * 2. filtre rapide cave / classique
   *
   * La recherche texte est supposée déjà gérée côté parent / backend,
   * donc ici on ne la refait pas pour ne pas casser ta logique existante.
   */
  const filteredProducts = useMemo(() => {
    let list = Array.isArray(products) ? [...products] : [];

    if (categoryFilter) {
      list = list.filter((p) => getProductCategory(p) === categoryFilter);
    }

    if (quickFilter === "cave") {
      list = list.filter(isCaveProduct);
    } else if (quickFilter === "classic") {
      list = list.filter((p) => !isCaveProduct(p));
    }

    return list;
  }, [products, categoryFilter, quickFilter]);

  /**
   * ============================================================
   * ZONE 8 — MAPPING ACTUEL DES SLOTS
   * ============================================================
   *
   * On relit le mapping existant pour afficher
   * quel produit est actuellement placé sur quel slot.
   */
  const fetchSlotMapping = async () => {
    try {
      const res = await fetch(
        "https://marchedeluxe.ch/services/get_platter_mapping.php",
        {
          cache: "no-store",
        }
      );
      const data = await res.json();
      if (data && typeof data === "object") {
        setSlotMapping(data);
      }
    } catch (e) {
      console.error("Erreur mapping plateau:", e);
    }
  };

  useEffect(() => {
    fetchSlotMapping();
  }, []);

  /**
   * ============================================================
   * ZONE 9 — MODALE D’ASSIGNATION
   * ============================================================
   *
   * Si le produit est cave :
   * - on propose le plateau vin en priorité
   *
   * Si le produit est classique :
   * - on propose le plateau fromage en priorité
   */
  const openAssignModal = (product) => {
    setAssignProduct(enrichProductWithScope(product));
    setAssignError("");
    setAssignModalOpen(true);
  };

  const closeAssignModal = () => {
    if (assignLoading) return;
    setAssignModalOpen(false);
    setAssignProduct(null);
    setAssignError("");
  };

  const assignToSlot = async (slotId) => {
    if (!assignProduct?.id || !slotId) return;

    try {
      setAssignLoading(true);
      setAssignError("");

      const token = localStorage.getItem("user_token") || "";

      const res = await fetch(
        "https://marchedeluxe.ch/services/set_platter_slot.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            slot_id: slotId,
            product_id: Number(assignProduct.id),
            platter_type: assignProductIsCave ? "wine" : "cheese",
          }),
        }
      );

      const data = await res.json();

      if (!res.ok || !data?.success) {
        setAssignError(data?.error || "Impossible d’assigner ce produit.");
        return;
      }

      await fetchSlotMapping();
      closeAssignModal();
    } catch (e) {
      console.error(e);
      setAssignError("Erreur réseau pendant l’assignation.");
    } finally {
      setAssignLoading(false);
    }
  };

  /**
   * ============================================================
   * ZONE 10 — PETITS HELPERS D’AFFICHAGE
   * ============================================================
   */
  const renderSortArrow = (key) => {
    if (sortConfig?.key !== key) return "⇅";
    return sortConfig?.direction === "asc" ? "↑" : "↓";
  };

  const formatPrice = (raw) => {
    const cents = Number(raw || 0);
    return `CHF ${(cents / 100).toFixed(2)}`;
  };

  const assignProductIsCave = isCaveProduct(assignProduct);

  const primarySlotsForModal = assignProductIsCave
    ? WINE_SLOT_OPTIONS
    : CHEESE_SLOT_OPTIONS;

  const secondarySlotsForModal = assignProductIsCave
    ? CHEESE_SLOT_OPTIONS
    : WINE_SLOT_OPTIONS;

  const primarySlotsTitle = assignProductIsCave
    ? "Plateau vin"
    : "Plateau fromage";

  const secondarySlotsTitle = assignProductIsCave
    ? "Plateau fromage"
    : "Plateau vin";

  return (
    <div style={styles.page}>
      {/* ==========================================================
          EN-TÊTE
         ========================================================== */}
      <div style={styles.headerRow}>
        <div>
          <h1 style={styles.title}>Dashboard Admin</h1>
          <div style={styles.subtitle}>
            Gestion de l’inventaire, promos, mise en ligne, produits cave et
            plateaux.
          </div>
        </div>

        {/* ========================================================
            BOUTONS D’AJOUT
            - ajoute produit cave => onAdd("cave")
            - ajoute produit classique => onAdd("classic")
           ======================================================== */}
        <div style={styles.headerActions}>
          {typeof onOpenHomeBanner === "function" && (
            <button
              type="button"
              style={styles.pageLinkBtn}
              onClick={() => onOpenHomeBanner()}
            >
              Gérer la bannière promo
            </button>
          )}

          <button
            type="button"
            style={styles.addBtnSecondary}
            onClick={() => onAdd?.("cave")}
          >
            + Ajouter un produit cave
          </button>

          <button
            type="button"
            style={styles.addBtn}
            onClick={() => onAdd?.("classic")}
          >
            + Ajouter un produit
          </button>
        </div>
      </div>

      {/* ==========================================================
          STATS
         ========================================================== */}
      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Produits</div>
          <div style={styles.statValue}>{Number(stats?.total || 0)}</div>
        </div>

        <div style={styles.statCard}>
          <div style={styles.statLabel}>Ruptures</div>
          <div style={styles.statValue}>{Number(stats?.ruptures || 0)}</div>
        </div>

        <div style={styles.statCard}>
          <div style={styles.statLabel}>Promotions</div>
          <div style={styles.statValue}>{Number(stats?.promos || 0)}</div>
        </div>

        <div style={styles.statCard}>
          <div style={styles.statLabel}>Produits cave</div>
          <div style={styles.statValue}>{caveCount}</div>
        </div>

        <div style={styles.statCard}>
          <div style={styles.statLabel}>Produits classiques</div>
          <div style={styles.statValue}>{classicCount}</div>
        </div>
      </div>

      {/* ==========================================================
          ACCÈS RAPIDE PAGES MÉTIER
         ========================================================== */}
      {typeof onOpenHomeBanner === "function" && (
        <div style={styles.quickPagesWrap}>
          <div style={styles.quickPagesTitle}>Pages métier</div>

          <div style={styles.quickPagesRow}>
            <button
              type="button"
              style={styles.quickPageCard}
              onClick={() => onOpenHomeBanner()}
            >
              <div style={styles.quickPageCardKicker}>Marketing</div>
              <div style={styles.quickPageCardTitle}>Bannière promo</div>
              <div style={styles.quickPageCardText}>
                Choisir les produits affichés, définir l’ordre et gérer les
                groupes anti-doublons.
              </div>
            </button>
          </div>
        </div>
      )}

      {/* ==========================================================
          OUTILS DE FILTRE
         ========================================================== */}
      <div style={styles.toolbar}>
        <input
          type="text"
          placeholder="Rechercher un produit…"
          value={recherche}
          onChange={(e) => setRecherche?.(e.target.value)}
          style={styles.searchInput}
        />

        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter?.(e.target.value)}
          style={styles.select}
        >
          <option value="">Toutes les catégories</option>
          {categories.map((cat) => (
            <option key={cat} value={cat}>
              {isCaveCategory(cat) ? `🍷 ${cat}` : cat}
            </option>
          ))}
        </select>
      </div>

      {/* ==========================================================
          FILTRES RAPIDES
         ========================================================== */}
      <div style={styles.quickFilterRow}>
        <button
          type="button"
          style={{
            ...styles.quickFilterBtn,
            ...(quickFilter === "all" ? styles.quickFilterActive : {}),
          }}
          onClick={() => setQuickFilter("all")}
        >
          Tous
        </button>

        <button
          type="button"
          style={{
            ...styles.quickFilterBtn,
            ...(quickFilter === "classic" ? styles.quickFilterActive : {}),
          }}
          onClick={() => setQuickFilter("classic")}
        >
          Classiques
        </button>

        <button
          type="button"
          style={{
            ...styles.quickFilterBtn,
            ...(quickFilter === "cave" ? styles.quickFilterActive : {}),
          }}
          onClick={() => setQuickFilter("cave")}
        >
          Cave
        </button>
      </div>

      {/* ==========================================================
          MAPPING ACTUEL DES PLATEAUX
         ========================================================== */}
      <div style={styles.mappingWrap}>
        <div style={styles.mappingTitle}>Mapping plateaux actuel</div>

        <div style={styles.mappingGrid}>
          <div style={styles.mappingBlock}>
            <div style={styles.mappingBlockTitle}>Plateau fromage</div>

            <div style={styles.mappingSlots}>
              {CHEESE_SLOT_OPTIONS.map((slot) => (
                <div key={slot.id} style={styles.mappingSlotCard}>
                  <div style={styles.mappingSlotId}>{slot.id}</div>
                  <div style={styles.mappingSlotLabel}>{slot.label}</div>
                  <div style={styles.mappingSlotProduct}>
                    {slotMapping?.[slot.id]?.name || "—"}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={styles.mappingBlock}>
            <div style={styles.mappingBlockTitle}>Plateau vin</div>

            <div style={styles.mappingSlots}>
              {WINE_SLOT_OPTIONS.map((slot) => (
                <div key={slot.id} style={styles.mappingSlotCard}>
                  <div style={styles.mappingSlotId}>{slot.id}</div>
                  <div style={styles.mappingSlotLabel}>{slot.label}</div>
                  <div style={styles.mappingSlotProduct}>
                    {slotMapping?.[slot.id]?.name || "—"}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ==========================================================
          TABLEAU PRODUITS
         ========================================================== */}
      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>
                <button style={styles.sortBtn} onClick={() => onSort?.("id")}>
                  ID {renderSortArrow("id")}
                </button>
              </th>

              <th style={styles.th}>
                <button style={styles.sortBtn} onClick={() => onSort?.("name")}>
                  Produit {renderSortArrow("name")}
                </button>
              </th>

              <th style={styles.th}>
                <button
                  style={styles.sortBtn}
                  onClick={() => onSort?.("category")}
                >
                  Catégorie {renderSortArrow("category")}
                </button>
              </th>

              <th style={styles.th}>
                <button
                  style={styles.sortBtn}
                  onClick={() => onSort?.("price")}
                >
                  Prix {renderSortArrow("price")}
                </button>
              </th>

              <th style={styles.th}>Stock</th>
              <th style={styles.th}>Online</th>
              <th style={styles.th}>Promo</th>
              <th style={styles.th}>Plateau</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>

          <tbody>
            {filteredProducts.map((p) => {
              const stockValue = Number(p?.stock || 0);
              const isOnline = Number(p?.isOnline || 0) === 1;
              const isPromo = Number(p?.isDiscount || 0) === 1;
              const caveProduct = isCaveProduct(p);
              const productForAction = enrichProductWithScope(p);

              return (
                <tr key={p.id} style={styles.tr}>
                  <td style={styles.td}>{p.id}</td>

                  <td style={styles.td}>
                    <div style={styles.productNameRow}>
                      <div style={{ fontWeight: 700 }}>{p.name}</div>

                      {caveProduct ? (
                        <span style={styles.caveBadge}>Cave</span>
                      ) : (
                        <span style={styles.classicBadge}>Classique</span>
                      )}
                    </div>

                    {!!p?.image && (
                      <div style={styles.smallText}>{p.image}</div>
                    )}
                  </td>

                  <td style={styles.td}>{getProductCategory(p) || "—"}</td>

                  <td style={styles.td}>
                    <div>{formatPrice(p.price)}</div>
                    {Number(p?.old_price || 0) > 0 && (
                      <div style={styles.oldPrice}>
                        {formatPrice(p.old_price)}
                      </div>
                    )}
                  </td>

                  <td style={styles.td}>
                    <button
                      type="button"
                      style={{
                        ...styles.toggleBtn,
                        ...(stockValue > 0
                          ? styles.toggleOnGreen
                          : styles.toggleOff),
                      }}
                      onClick={() => onToggleStock?.(p.id, stockValue)}
                    >
                      {stockValue > 0 ? "En stock" : "Rupture"}
                    </button>
                  </td>

                  <td style={styles.td}>
                    <button
                      type="button"
                      style={{
                        ...styles.toggleBtn,
                        ...(isOnline ? styles.toggleOnBlue : styles.toggleOff),
                      }}
                      onClick={() => onToggleOnline?.(p.id, isOnline ? 0 : 1)}
                    >
                      {isOnline ? "Oui" : "Non"}
                    </button>
                  </td>

                  <td style={styles.td}>
                    <button
                      type="button"
                      style={{
                        ...styles.toggleBtn,
                        ...(isPromo ? styles.toggleOnGold : styles.toggleOff),
                      }}
                      onClick={() => onTogglePromo?.(p.id, isPromo ? 0 : 1)}
                    >
                      {isPromo ? "Promo" : "Standard"}
                    </button>
                  </td>

                  <td style={styles.td}>
                    <button
                      type="button"
                      style={{
                        ...styles.assignBtn,
                        ...(caveProduct ? styles.assignBtnWine : {}),
                      }}
                      onClick={() => openAssignModal(productForAction)}
                    >
                      {caveProduct ? "Assigner vin" : "Assigner"}
                    </button>
                  </td>

                  <td style={styles.td}>
                    <div style={styles.actionRow}>
                      <button
                        type="button"
                        style={styles.editBtn}
                        onClick={() =>
                          onEdit?.(
                            productForAction,
                            caveProduct ? "cave" : "classic"
                          )
                        }
                      >
                        Éditer
                      </button>

                      <button
                        type="button"
                        style={styles.deleteBtn}
                        onClick={() =>
                          onDelete?.(
                            p.id,
                            caveProduct ? "cave" : "classic",
                            productForAction
                          )
                        }
                      >
                        Supprimer
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}

            {!filteredProducts.length && (
              <tr>
                <td style={styles.emptyCell} colSpan={9}>
                  Aucun produit trouvé.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ==========================================================
          MODALE ASSIGNATION SLOT
         ========================================================== */}
      {assignModalOpen && assignProduct && (
        <div style={styles.modalBackdrop} onClick={closeAssignModal}>
          <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalTitle}>Assigner au plateau</div>

            <div style={styles.modalSubtext}>
              Produit sélectionné : <strong>{assignProduct.name}</strong>
            </div>

            <div style={styles.modalSubtext}>
              Type :{" "}
              <strong>
                {assignProductIsCave ? "Produit cave" : "Produit classique"}
              </strong>
            </div>

            {assignError ? (
              <div style={styles.modalError}>{assignError}</div>
            ) : null}

            {/* ======================================================
                SECTION PRINCIPALE
               ====================================================== */}
            <div style={{ marginTop: 8 }}>
              <div style={styles.slotSectionTitle}>{primarySlotsTitle}</div>
              <div style={styles.slotGrid}>
                {primarySlotsForModal.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    style={{
                      ...styles.slotBtn,
                      ...(assignProductIsCave
                        ? styles.slotBtnWinePrimary
                        : styles.slotBtnCheesePrimary),
                    }}
                    onClick={() => assignToSlot(s.id)}
                    title={s.id}
                    disabled={assignLoading}
                  >
                    <div style={{ fontWeight: 800 }}>{s.label}</div>
                    <div style={{ fontSize: 12, opacity: 0.75 }}>{s.id}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* ======================================================
                SECTION SECONDAIRE
               ====================================================== */}
            <div style={{ marginTop: 14 }}>
              <div style={styles.slotSectionTitle}>{secondarySlotsTitle}</div>
              <div style={styles.slotGrid}>
                {secondarySlotsForModal.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    style={styles.slotBtn}
                    onClick={() => assignToSlot(s.id)}
                    title={s.id}
                    disabled={assignLoading}
                  >
                    <div style={{ fontWeight: 800 }}>{s.label}</div>
                    <div style={{ fontSize: 12, opacity: 0.75 }}>{s.id}</div>
                  </button>
                ))}
              </div>
            </div>

            <div style={styles.modalFooter}>
              <button
                type="button"
                style={styles.closeBtn}
                onClick={closeAssignModal}
                disabled={assignLoading}
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * ============================================================
 * ZONE 11 — STYLES
 * ============================================================
 *
 * Tout est centralisé ici pour que tu puisses ajuster vite :
 * - couleurs
 * - badges cave / classique
 * - boutons
 * - table
 */
const styles = {
  page: {
    width: "100%",
  },

  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 16,
    flexWrap: "wrap",
    marginBottom: 22,
  },

  headerActions: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
  },

  title: {
    margin: 0,
    fontSize: 28,
    fontWeight: 900,
    color: "#1a1a1a",
  },

  subtitle: {
    marginTop: 6,
    fontSize: 14,
    opacity: 0.75,
  },

  addBtn: {
    border: "none",
    borderRadius: 12,
    padding: "12px 16px",
    background: "#d4af37",
    color: "#111",
    fontWeight: 800,
    cursor: "pointer",
  },

  addBtnSecondary: {
    border: "1px solid rgba(125,94,55,.16)",
    borderRadius: 12,
    padding: "12px 16px",
    background: "#fff",
    color: "#5d4027",
    fontWeight: 800,
    cursor: "pointer",
  },

  pageLinkBtn: {
    border: "1px solid rgba(212,175,55,.28)",
    borderRadius: 12,
    padding: "12px 16px",
    background: "rgba(212,175,55,.10)",
    color: "#7a5a10",
    fontWeight: 900,
    cursor: "pointer",
  },

  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 14,
    marginBottom: 20,
  },

  statCard: {
    background: "#fff",
    border: "1px solid rgba(0,0,0,.08)",
    borderRadius: 16,
    padding: 16,
    boxShadow: "0 8px 24px rgba(0,0,0,.04)",
  },

  statLabel: {
    fontSize: 13,
    opacity: 0.7,
    marginBottom: 8,
  },

  statValue: {
    fontSize: 28,
    fontWeight: 900,
  },

  quickPagesWrap: {
    marginBottom: 20,
    padding: 16,
    borderRadius: 16,
    background: "#fff",
    border: "1px solid rgba(0,0,0,.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,.04)",
  },

  quickPagesTitle: {
    fontWeight: 900,
    fontSize: 16,
    marginBottom: 12,
  },

  quickPagesRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: 12,
  },

  quickPageCard: {
    textAlign: "left",
    border: "1px solid rgba(212,175,55,.22)",
    borderRadius: 14,
    padding: 14,
    background:
      "linear-gradient(180deg, rgba(255,253,247,1) 0%, rgba(252,248,236,1) 100%)",
    cursor: "pointer",
  },

  quickPageCardKicker: {
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: ".08em",
    textTransform: "uppercase",
    color: "#8d6b00",
    marginBottom: 6,
  },

  quickPageCardTitle: {
    fontSize: 16,
    fontWeight: 900,
    color: "#1a1a1a",
    marginBottom: 6,
  },

  quickPageCardText: {
    fontSize: 13,
    lineHeight: 1.45,
    color: "#5e564d",
  },

  toolbar: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 14,
  },

  searchInput: {
    flex: "1 1 320px",
    minWidth: 240,
    border: "1px solid rgba(0,0,0,.12)",
    borderRadius: 12,
    padding: "12px 14px",
    fontSize: 14,
    outline: "none",
    background: "#fff",
  },

  select: {
    minWidth: 220,
    border: "1px solid rgba(0,0,0,.12)",
    borderRadius: 12,
    padding: "12px 14px",
    fontSize: 14,
    outline: "none",
    background: "#fff",
  },

  quickFilterRow: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    marginBottom: 20,
  },

  quickFilterBtn: {
    border: "1px solid rgba(0,0,0,.10)",
    borderRadius: 999,
    padding: "8px 14px",
    background: "#fff",
    color: "#333",
    fontWeight: 700,
    cursor: "pointer",
  },

  quickFilterActive: {
    background: "#1a1a1a",
    color: "#fff",
    border: "1px solid #1a1a1a",
  },

  mappingWrap: {
    marginBottom: 24,
    padding: 16,
    borderRadius: 16,
    background: "#fff",
    border: "1px solid rgba(0,0,0,.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,.04)",
  },

  mappingTitle: {
    fontWeight: 900,
    fontSize: 16,
    marginBottom: 14,
  },

  mappingGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: 18,
  },

  mappingBlock: {
    border: "1px solid rgba(0,0,0,.08)",
    borderRadius: 14,
    padding: 14,
    background: "#fcfcfc",
  },

  mappingBlockTitle: {
    fontWeight: 800,
    marginBottom: 12,
  },

  mappingSlots: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
    gap: 10,
  },

  mappingSlotCard: {
    border: "1px solid rgba(0,0,0,.08)",
    borderRadius: 12,
    padding: 10,
    background: "#fff",
  },

  mappingSlotId: {
    fontWeight: 900,
    fontSize: 12,
    color: "#b8962d",
    marginBottom: 4,
  },

  mappingSlotLabel: {
    fontSize: 12,
    opacity: 0.75,
    marginBottom: 8,
  },

  mappingSlotProduct: {
    fontWeight: 700,
    fontSize: 13,
  },

  tableWrap: {
    width: "100%",
    overflowX: "auto",
    borderRadius: 16,
    background: "#fff",
    border: "1px solid rgba(0,0,0,.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,.04)",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: 1080,
  },

  th: {
    textAlign: "left",
    padding: "14px 12px",
    borderBottom: "1px solid rgba(0,0,0,.08)",
    background: "#faf8f2",
    fontSize: 13,
    fontWeight: 800,
    whiteSpace: "nowrap",
  },

  td: {
    padding: "14px 12px",
    borderBottom: "1px solid rgba(0,0,0,.06)",
    verticalAlign: "middle",
    fontSize: 14,
  },

  tr: {
    background: "#fff",
  },

  sortBtn: {
    border: "none",
    background: "transparent",
    padding: 0,
    margin: 0,
    fontWeight: 800,
    cursor: "pointer",
    color: "#1a1a1a",
  },

  productNameRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },

  caveBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 22,
    padding: "0 8px",
    borderRadius: 999,
    background: "rgba(125,94,55,.10)",
    color: "#6f4d2e",
    fontSize: 11,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: ".04em",
  },

  classicBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 22,
    padding: "0 8px",
    borderRadius: 999,
    background: "rgba(0,0,0,.06)",
    color: "#555",
    fontSize: 11,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: ".04em",
  },

  smallText: {
    fontSize: 12,
    opacity: 0.6,
    marginTop: 4,
    wordBreak: "break-all",
  },

  oldPrice: {
    fontSize: 12,
    opacity: 0.65,
    textDecoration: "line-through",
    marginTop: 4,
  },

  toggleBtn: {
    border: "none",
    borderRadius: 999,
    padding: "8px 12px",
    fontWeight: 800,
    cursor: "pointer",
    fontSize: 12,
    whiteSpace: "nowrap",
  },

  toggleOnGreen: {
    background: "rgba(39,174,96,.14)",
    color: "#1f7d48",
  },

  toggleOnBlue: {
    background: "rgba(52,152,219,.14)",
    color: "#21618c",
  },

  toggleOnGold: {
    background: "rgba(212,175,55,.18)",
    color: "#8d6b00",
  },

  toggleOff: {
    background: "rgba(0,0,0,.06)",
    color: "#555",
  },

  assignBtn: {
    border: "1px solid rgba(0,0,0,.12)",
    borderRadius: 10,
    padding: "8px 12px",
    background: "#fff",
    fontWeight: 700,
    cursor: "pointer",
  },

  assignBtnWine: {
    border: "1px solid rgba(125,94,55,.18)",
    color: "#6f4d2e",
    background: "rgba(125,94,55,.05)",
  },

  actionRow: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
  },

  editBtn: {
    border: "none",
    borderRadius: 10,
    padding: "8px 12px",
    background: "#1a1a1a",
    color: "#fff",
    fontWeight: 700,
    cursor: "pointer",
  },

  deleteBtn: {
    border: "none",
    borderRadius: 10,
    padding: "8px 12px",
    background: "#b93b3b",
    color: "#fff",
    fontWeight: 700,
    cursor: "pointer",
  },

  emptyCell: {
    textAlign: "center",
    padding: 30,
    opacity: 0.7,
  },

  modalBackdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 3000,
    padding: 20,
  },

  modalCard: {
    width: "100%",
    maxWidth: 860,
    background: "#fff",
    borderRadius: 18,
    padding: 22,
    boxShadow: "0 20px 60px rgba(0,0,0,.18)",
  },

  modalTitle: {
    fontWeight: 900,
    fontSize: 22,
    marginBottom: 8,
  },

  modalSubtext: {
    fontSize: 14,
    opacity: 0.8,
    marginBottom: 10,
  },

  modalError: {
    marginTop: 10,
    marginBottom: 10,
    padding: 10,
    borderRadius: 10,
    background: "rgba(185,59,59,.08)",
    color: "#9c1f1f",
    fontSize: 13,
    fontWeight: 700,
  },

  slotSectionTitle: {
    fontWeight: 800,
    fontSize: 14,
    marginBottom: 10,
  },

  slotGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: 10,
  },

  slotBtn: {
    border: "1px solid rgba(0,0,0,.08)",
    borderRadius: 12,
    padding: 14,
    background: "#fcfbf7",
    textAlign: "left",
    cursor: "pointer",
  },

  slotBtnWinePrimary: {
    border: "1px solid rgba(125,94,55,.18)",
    background: "rgba(125,94,55,.05)",
  },

  slotBtnCheesePrimary: {
    border: "1px solid rgba(212,175,55,.18)",
    background: "rgba(212,175,55,.08)",
  },

  modalFooter: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: 18,
  },

  closeBtn: {
    border: "1px solid rgba(0,0,0,.12)",
    borderRadius: 10,
    padding: "10px 14px",
    background: "#fff",
    fontWeight: 700,
    cursor: "pointer",
  },
};

export default AdminDashboard;
